/**
 * SparkleBtn — thin polymorphic wrapper that adds hover / press sparkles
 * on top of any interactive element. Everything about the child
 * (styling, aria, testid, onClick, etc.) is preserved; the wrapper
 * only owns the sparkle overlay + trigger wiring.
 *
 * Usage:
 *   <SparkleBtn as="button" className="..." onClick={...}>
 *     <Icon />
 *   </SparkleBtn>
 *
 * Under the hood: renders the requested tag (default ``button``),
 * attaches ``onPointerEnter`` + ``onPointerDown`` (chained with any
 * caller-supplied handlers), and injects a ``<SparkleBurst>`` layer
 * as a sibling of the children. Silent no-op under
 * ``prefers-reduced-motion``.
 */
import { forwardRef } from "react";
import SparkleBurst, { useSparkleTrigger } from "./SparkleBurst";

const SparkleBtn = forwardRef(function SparkleBtn(
  {
    as: Tag = "button",
    className = "",
    children,
    onPointerEnter,
    onPointerDown,
    variant = "dot",
    ...rest
  },
  ref,
) {
  const sparkle = useSparkleTrigger();
  const chain = (mine, external) => (e) => {
    mine(e);
    if (external) external(e);
  };
  return (
    <Tag
      ref={ref}
      className={"relative " + className}
      onPointerEnter={chain(sparkle.onEnter, onPointerEnter)}
      onPointerDown={chain(sparkle.onPress, onPointerDown)}
      {...rest}
    >
      {children}
      <SparkleBurst trigger={sparkle.count} variant={variant} />
    </Tag>
  );
});

export default SparkleBtn;
